import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import ResizeDetector from 'react-resize-detector';
import { Route, Switch, withRouter } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { withTranslation } from 'react-i18next';

import ProtectedRoute from './ProtectedRoute';
import AuthApp from './AuthApp';
import ServicesApp from './ServicesApp';
import OrganizationApp from './OrganizationApp';
import PartnerApp from './PartnerApp';
import UserApp from './UserApp';
import { AuthContext } from 'context/AuthContext';
import Loading from 'Layout/Components/Loading';

// page requested before the login
const REDIRECT_KEY = 'redirectAfterLogin';

// role required by each app path
const APP_ROLES = {
  '/admin': 'EmailVeritas Admin',
  '/organization': 'Organization Admin',
  '/partner': 'Partner Admin',
  '/user': 'User'
};

// links of e-mails sent before the API used the /auth routes
const LEGACY_AUTH_ROUTES = {
  '/users/reset-password': '/auth/reset-password',
  '/confirm-email': '/auth/confirm-email',
};

function legacyAuthRoute(pathName) {
  const legacy = Object.keys(LEGACY_AUTH_ROUTES).find(route => pathName.startsWith(route));
  return legacy ? LEGACY_AUTH_ROUTES[legacy] : null;
}

class App extends React.Component {

  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      closedSmallerSidebar: false
    };
  }

  componentDidMount() {
    let pathName = this.props.location.pathname;
    const legacy = legacyAuthRoute(pathName);
    if (legacy) {
      pathName = legacy;
      this.props.history.replace(pathName + this.props.location.search);
    }
    if (pathName.startsWith('/auth/confirm-email')) {
      this.context.confirmEmail();
    } else if (pathName.startsWith('/auth/reset-password')) {
      this.context.resetPassword();
    }
  }

  navigateToUserHome() {
    const {location, history } = this.props;
    const pathName = location.pathname;
    if (pathName.startsWith('/admin') || pathName.startsWith('/organization')
      || pathName.startsWith('/partner') || pathName.startsWith('/user')) {
      return; // its already inside the app
    }
    const user = this.context.user;
    const roles = user.roles || [];
    // continue to the page requested before the login (deep link)
    const redirect = sessionStorage.getItem(REDIRECT_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
    const redirectRole = redirect && Object.keys(APP_ROLES).find(app => redirect.startsWith(app));
    if (redirectRole && roles.includes(APP_ROLES[redirectRole])) {
      history.push(redirect);
    } else if (roles.includes('EmailVeritas Admin')) {
      history.push('/admin/services');
    } else if (roles.includes('Organization Admin')) {
      history.push('/organization');
    } else if (roles.includes('Partner Admin')) {
      history.push('/partner');
    } /*else if (roles.includes('User')) {
      history.push('/user/dashboard');
    }*/ else {
      toast["error"](this.props.t('login.error'));
      this.context.doLogout();
    }
  }

  navigateToAuth() {
    const pathName = this.props.location.pathname;
    if (pathName.startsWith('/auth') || legacyAuthRoute(pathName)) {
      return; // its already inside the app (componentDidMount maps a legacy route)
    }
    if (Object.keys(APP_ROLES).some(app => pathName.startsWith(app))) {
      // remember the requested page to continue after the login
      sessionStorage.setItem(REDIRECT_KEY, pathName + this.props.location.search);
    }
    let history = this.props.history;
    history.push(`/auth/${this.context.emailAuthRoute()}`);
  }

  render() {
    let {
      colorScheme,
      enableFixedHeader,
      enableFixedSidebar,
      enableFixedFooter,
      enableClosedSidebar,
      closedSmallerSidebar,
      enableMobileMenu,
      enablePageTabsAlt,
    } = this.props;

    if (this.context.authenticated()) {
      return (
        <ResizeDetector handleWidth render={({ width }) => (
          <Fragment>
            <div className={cx(
              "app-container app-theme-" + colorScheme,
              { 'fixed-header': enableFixedHeader },
              { 'fixed-sidebar': enableFixedSidebar || width < 1250 },
              { 'fixed-footer': enableFixedFooter },
              { 'closed-sidebar': enableClosedSidebar || width < 1250 },
              { 'closed-sidebar-mobile': closedSmallerSidebar || width < 1250 },
              { 'sidebar-mobile-open': enableMobileMenu },
              { 'body-tabs-shadow-btn': enablePageTabsAlt },
            )}>
              <Fragment>
                <Switch>
                  <ProtectedRoute path={'/admin'} role="EmailVeritas Admin" component={ServicesApp} />
                  <ProtectedRoute path={'/organization'} role="Organization Admin" component={OrganizationApp} />
                  <ProtectedRoute path={'/partner'} role="Partner Admin" component={PartnerApp} />
                  <ProtectedRoute path={'/user'} role="User" component={UserApp} />
                </Switch>
                {this.navigateToUserHome()}
              </Fragment>
            </div>
            <ToastContainer />
          </Fragment>
        )}
        />
      )
    } else if (this.context.inProgress()) {
      return (<Loading />)
    } else {
      return (
        <Fragment>
          <Switch>
            <Route path={'/auth'}>
              <AuthApp />
            </Route>
          </Switch>
          {this.navigateToAuth()}
        </Fragment>
      )
    }
  }
}

const mapStateToProp = state => ({
  colorScheme: state.ThemeOptions.colorScheme,
  enableFixedHeader: state.ThemeOptions.enableFixedHeader,
  enableMobileMenu: state.ThemeOptions.enableMobileMenu,
  enableFixedFooter: state.ThemeOptions.enableFixedFooter,
  enableFixedSidebar: state.ThemeOptions.enableFixedSidebar,
  enableClosedSidebar: state.ThemeOptions.enableClosedSidebar,
  enablePageTabsAlt: state.ThemeOptions.enablePageTabsAlt
});

export default connect(mapStateToProp)(withRouter(withTranslation()(App)));
